import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Phone, Mail, User, LogOut, ChevronDown } from 'lucide-react';
import { isAuthenticated, logout, getUser } from '../../utils/auth';

const NavbarSimple = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  let user = null;
  try { user = getUser(); } catch { user = null; }
  const isArabic = i18n.language === 'ar';

  const toggleLanguage = () => {
    const newLang = isArabic ? 'en' : 'ar';
    i18n.changeLanguage(newLang);
    document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLang;
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const text = {
    home: isArabic ? '\u0627\u0644\u0631\u0626\u064a\u0633\u064a\u0629' : 'Home',
    services: isArabic ? '\u062e\u062f\u0645\u0627\u062a\u0646\u0627' : 'Services',
    about: isArabic ? '\u0639\u0646 \u0648\u0627\u0632\u0646' : 'About',
    resources: isArabic ? '\u0627\u0644\u0645\u0648\u0627\u0631\u062f' : 'Resources',
    join: isArabic ? '\u0627\u0646\u0636\u0645 \u0625\u0644\u0649 \u0648\u0627\u0632\u0646' : 'Join Wazin',
    contact: isArabic ? '\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627' : 'Contact',
    account: isArabic ? '\u0627\u0644\u062d\u0633\u0627\u0628' : 'Account',
    logout: isArabic ? '\u062e\u0631\u0648\u062c' : 'Logout',
    platform: isArabic ? '\u0645\u0646\u0635\u0629 \u0648\u0627\u0632\u0646' : 'Wazin Platform',
    languageSwitch: isArabic ? '\u0627\u0644\u0639\u0631\u0628\u064a\u0629 | EN' : 'AR | EN',
  };

  const megaMenuData = {
    services: {
      title: text.services,
      sections: [
        {
          heading: isArabic ? '\u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629' : 'Legal Services',
          description: isArabic
            ? '\u062d\u0644\u0648\u0644 \u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u0645\u062a\u0643\u0627\u0645\u0644\u0629 \u0644\u0644\u0623\u0641\u0631\u0627\u062f \u0648\u0627\u0644\u0634\u0631\u0643\u0627\u062a'
            : 'Integrated legal solutions for individuals and businesses',
          links: [
            { name: isArabic ? '\u0627\u0644\u0627\u0633\u062a\u0634\u0627\u0631\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629' : 'Legal Consultations', path: '/book-consultation' },
            { name: isArabic ? '\u0635\u064a\u0627\u063a\u0629 \u0648\u0645\u0631\u0627\u062c\u0639\u0629 \u0627\u0644\u0639\u0642\u0648\u062f' : 'Contract Drafting & Review', path: '/services/contracts' },
            { name: isArabic ? '\u0627\u0644\u062a\u0645\u062b\u064a\u0644 \u0627\u0644\u0642\u0636\u0627\u0626\u064a' : 'Litigation Representation', path: '/services/litigation' },
            { name: isArabic ? '\u0627\u0644\u0639\u0642\u0648\u062f \u0627\u0644\u0631\u0642\u0645\u064a\u0629' : 'Digital Contracts', path: '/services/contracts' },
          ],
        },
        {
          heading: isArabic ? '\u0627\u062e\u062a\u0635\u0627\u0635\u0627\u062a \u0627\u0644\u062a\u0642\u0627\u0636\u064a' : 'Litigation Specialties',
          description: isArabic
            ? '\u062a\u0645\u062b\u064a\u0644 \u0642\u0627\u0646\u0648\u0646\u064a \u0641\u064a \u0645\u062e\u062a\u0644\u0641 \u0623\u0646\u0648\u0627\u0639 \u0627\u0644\u0642\u0636\u0627\u064a\u0627'
            : 'Legal representation in different types of disputes',
          links: [
            { name: isArabic ? '\u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0627\u0644\u062a\u062c\u0627\u0631\u064a\u0629' : 'Commercial Cases', path: '/services/litigation' },
            { name: isArabic ? '\u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0627\u0644\u0623\u0633\u0631\u064a\u0629' : 'Family Cases', path: '/services/litigation' },
            { name: isArabic ? '\u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0627\u0644\u0639\u0645\u0627\u0644\u064a\u0629' : 'Labor Cases', path: '/services/litigation' },
            { name: isArabic ? '\u0627\u0644\u0642\u0636\u0627\u064a\u0627 \u0627\u0644\u0639\u0642\u0627\u0631\u064a\u0629' : 'Real Estate Cases', path: '/services/litigation' },
          ],
        },
        {
          heading: isArabic ? '\u0627\u0644\u062e\u062f\u0645\u0627\u062a \u0627\u0644\u0645\u0624\u0633\u0633\u064a\u0629' : 'Corporate Services',
          description: isArabic
            ? '\u062f\u0639\u0645 \u0642\u0627\u0646\u0648\u0646\u064a \u0645\u062a\u0643\u0627\u0645\u0644 \u0644\u0642\u0637\u0627\u0639 \u0627\u0644\u0623\u0639\u0645\u0627\u0644'
            : 'Integrated legal support for the business sector',
          links: [
            { name: isArabic ? '\u062a\u0623\u0633\u064a\u0633 \u0627\u0644\u0634\u0631\u0643\u0627\u062a' : 'Company Formation', path: '/book-consultation' },
            { name: isArabic ? '\u0627\u0644\u0627\u0645\u062a\u062b\u0627\u0644 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a' : 'Legal Compliance', path: '/services/compliance' },
            { name: isArabic ? '\u0627\u0644\u0645\u0644\u0643\u064a\u0629 \u0627\u0644\u0641\u0643\u0631\u064a\u0629' : 'Intellectual Property', path: '/services/ip' },
          ],
        },
      ],
    },
    about: {
      title: text.about,
      sections: [
        {
          heading: isArabic ? '\u0645\u0646 \u0646\u062d\u0646' : 'Who We Are',
          description: isArabic
            ? '\u062a\u0639\u0631\u0641 \u0639\u0644\u0649 \u0647\u0648\u064a\u0629 \u0648\u0627\u0632\u0646 \u0648\u0631\u0633\u0627\u0644\u062a\u0647\u0627'
            : 'Discover Wazin identity and mission',
          links: [
            { name: isArabic ? '\u0646\u0628\u0630\u0629 \u0639\u0646 \u0648\u0627\u0632\u0646' : 'About Wazin', path: '/#about' },
            { name: isArabic ? '\u0631\u0624\u064a\u062a\u0646\u0627 \u0648\u0631\u0633\u0627\u0644\u062a\u0646\u0627' : 'Vision & Mission', path: '/#about' },
            { name: isArabic ? '\u0642\u064a\u0645\u0646\u0627' : 'Our Values', path: '/#about' },
            { name: isArabic ? '\u0625\u0646\u062c\u0627\u0632\u0627\u062a\u0646\u0627' : 'Achievements', path: '/#about' },
          ],
        },
        {
          heading: isArabic ? '\u0631\u0648\u0627\u0628\u0637 \u0645\u0647\u0645\u0629' : 'Important Links',
          description: isArabic
            ? '\u0627\u0633\u062a\u0643\u0634\u0641 \u0645\u0639\u0644\u0648\u0645\u0627\u062a \u0625\u0636\u0627\u0641\u064a\u0629 \u0639\u0646 \u0648\u0627\u0632\u0646'
            : 'Explore more useful information about Wazin',
          links: [
            { name: isArabic ? '\u0627\u0644\u0645\u0642\u0631 \u0627\u0644\u0631\u0626\u064a\u0633\u064a' : 'Head Office', path: '/#locations' },
            { name: isArabic ? '\u0639\u0645\u0644\u0627\u0621 \u0648\u062b\u0642\u0648\u0627 \u0628\u0646\u0627' : 'Trusted Clients', path: '/#client-logos' },
            { name: isArabic ? '\u0627\u0644\u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0634\u0627\u0626\u0639\u0629' : 'FAQ', path: '/#faq' },
            { name: isArabic ? '\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627' : 'Contact Us', path: '/#locations' },
          ],
        },
      ],
    },
    resources: {
      title: text.resources,
      sections: [
        {
          heading: isArabic ? '\u0627\u0644\u0645\u0639\u0631\u0641\u0629 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629' : 'Legal Knowledge',
          description: isArabic
            ? '\u0645\u0642\u0627\u0644\u0627\u062a \u0648\u0645\u062d\u062a\u0648\u0649 \u0642\u0627\u0646\u0648\u0646\u064a \u0645\u0648\u062b\u0648\u0642'
            : 'Trusted articles and legal content',
          links: [
            { name: isArabic ? '\u0627\u0644\u0645\u062f\u0648\u0646\u0629 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629' : 'Legal Blog', path: '/#news' },
            { name: isArabic ? '\u0645\u0633\u062a\u062c\u062f\u0627\u062a \u0648\u0627\u0632\u0646' : 'Wazin Updates', path: '/#news' },
            { name: isArabic ? '\u0645\u0628\u0627\u062f\u0631\u0627\u062a \u0648\u0627\u0632\u0646' : 'Wazin Initiatives', path: '/#news' },
            { name: isArabic ? '\u0627\u0644\u0645\u062d\u062a\u0648\u0649 \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a' : 'Legal Content', path: '/#news' },
          ],
        },
        {
          heading: isArabic ? '\u0645\u0631\u0643\u0632 \u0627\u0644\u0645\u0633\u0627\u0639\u062f\u0629' : 'Help Center',
          description: isArabic
            ? '\u0625\u062c\u0627\u0628\u0627\u062a \u0648\u0627\u0636\u062d\u0629 \u0639\u0644\u0649 \u0627\u0633\u062a\u0641\u0633\u0627\u0631\u0627\u062a\u0643'
            : 'Clear answers to your questions',
          links: [
            { name: isArabic ? '\u0627\u0644\u0623\u0633\u0626\u0644\u0629 \u0627\u0644\u0634\u0627\u0626\u0639\u0629' : 'FAQ', path: '/#faq' },
            { name: isArabic ? '\u062a\u0648\u0627\u0635\u0644 \u0645\u0639\u0646\u0627' : 'Contact Us', path: '/#locations' },
            { name: isArabic ? '\u0627\u0644\u0634\u0631\u0648\u0637 \u0648\u0627\u0644\u0623\u062d\u0643\u0627\u0645' : 'Terms & Conditions', path: '/terms-conditions' },
            { name: isArabic ? '\u0633\u064a\u0627\u0633\u0629 \u0627\u0644\u062e\u0635\u0648\u0635\u064a\u0629' : 'Privacy Policy', path: '/privacy-policy' },
          ],
        },
      ],
    },
    join: {
      title: text.join,
      sections: [
        {
          heading: isArabic ? '\u0641\u0631\u0635 \u0627\u0644\u0627\u0646\u0636\u0645\u0627\u0645' : 'Join Opportunities',
          description: isArabic
            ? '\u0645\u0633\u0627\u0631\u0627\u062a \u0645\u0647\u0646\u064a\u0629 \u0648\u062a\u062f\u0631\u064a\u0628\u064a\u0629 \u0636\u0645\u0646 \u0628\u064a\u0626\u0629 \u0648\u0627\u0632\u0646'
            : 'Career and training tracks within Wazin',
          links: [
            { name: isArabic ? '\u0627\u0644\u062a\u0648\u0638\u064a\u0641' : 'Careers', path: '/#about' },
            {
              name: isArabic
                ? '\u0627\u0644\u062a\u062f\u0631\u064a\u0628 \u0644\u0644\u062a\u0623\u0647\u064a\u0644 \u0644\u0644\u0642\u064a\u062f \u0628\u0648\u0632\u0627\u0631\u0629 \u0627\u0644\u0639\u062f\u0644 \u0644\u0645\u0632\u0627\u0648\u0644\u0629 \u0627\u0644\u0645\u0647\u0646\u0629'
                : 'Training for Ministry of Justice Registration',
              path: '/#about',
            },
            {
              name: isArabic
                ? '\u0627\u0644\u062a\u062f\u0631\u064a\u0628 \u0627\u0644\u062a\u0639\u0627\u0648\u0646\u064a (\u0644\u0644\u062c\u0627\u0645\u0639\u0627\u062a)'
                : 'Cooperative Training (Universities)',
              path: '/#about',
            },
          ],
        },
      ],
    },
  };

  const navLinks = [
    { name: text.home, path: '/', hasMegaMenu: false },
    { name: text.services, path: '/services', hasMegaMenu: true, menuKey: 'services' },
    { name: text.about, path: '/#about', hasMegaMenu: true, menuKey: 'about' },
    { name: text.resources, path: '/#news', hasMegaMenu: true, menuKey: 'resources' },
    { name: text.join, path: '/#about', hasMegaMenu: true, menuKey: 'join' },
    { name: text.contact, path: '/#contact', hasMegaMenu: false },
  ];

  const renderMegaMenu = (link) => {
    if (!link.hasMegaMenu || activeMenu !== link.menuKey) {
      return null;
    }

    const menu = megaMenuData[link.menuKey];
    const columnCount =
      menu.sections.length === 1 ? 'grid-cols-1' : menu.sections.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
    const menuWidth =
      menu.sections.length === 1 ? 'min-w-[360px]' : menu.sections.length === 2 ? 'min-w-[720px]' : 'min-w-[940px]';

    return (
      <div
        className={`absolute top-full ${isArabic ? 'right-0' : 'left-0'} mt-3 ${menuWidth} z-50`}
        onMouseEnter={() => setActiveMenu(link.menuKey)}
        onMouseLeave={() => setActiveMenu(null)}
      >
        <div className="rounded-[24px] border border-[#A27849]/15 bg-white shadow-[0_18px_40px_rgba(20,40,70,0.12)] overflow-hidden animate-[fadeIn_.16s_ease-out]">
          <div className="px-8 pt-6 pb-5 border-b border-slate-200">
            <h3 className="text-[1.35rem] font-bold text-[#1F3B64]">
              {menu.title}
            </h3>
          </div>

          <div className={`grid ${columnCount} gap-12 px-8 py-7`}>
            {menu.sections.map((section) => (
              <div key={section.heading}>
                <h4 className="text-[1.02rem] font-bold text-[#1F3B64] mb-1.5 tracking-[0.01em]">
                  {section.heading}
                </h4>
                <p className="text-[13px] leading-6 text-slate-600 mb-4">
                  {section.description}
                </p>
                <ul className="space-y-2.5">
                  {section.links.map((item) => (
                    <li key={`${section.heading}-${item.path}-${item.name}`}>
                      <Link
                        to={item.path}
                        className="group flex items-center gap-3 rounded-xl px-3 py-2 text-[14px] font-medium text-slate-800 hover:text-[#A27849] hover:bg-[#A27849]/6 transition-all duration-150"
                      >
                        <span className="h-1.5 w-1.5 rounded-full bg-[#A27849]/70 group-hover:bg-[#A27849] transition-colors" />
                        <span>{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <nav className="fixed top-0 w-full z-50 bg-white border-b border-[#A27849]/20 shadow-[0_10px_24px_rgba(18,36,64,0.08)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24 md:h-28" dir={isArabic ? 'rtl' : 'ltr'}>
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="/images/wazin-logo.png"
              alt="Wazin Logo"
              className="h-20 md:h-24 w-auto"
              onError={(e) => {
                e.target.src = '/images/wazin-icon.png';
              }}
            />
          </Link>

          <div className={`hidden md:flex items-center ${isArabic ? 'gap-7' : 'gap-5 xl:gap-6'}`}>
            {navLinks.map((link) => (
              <div
                key={link.name}
                className="relative"
                onMouseEnter={() => link.hasMegaMenu && setActiveMenu(link.menuKey)}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <Link
                  to={link.path}
                  className={`flex items-center gap-1.5 whitespace-nowrap py-2 font-bold text-[#1F3B64] hover:text-[#A27849] transition-colors duration-150 ${isArabic ? 'text-[15px]' : 'text-[14px] xl:text-[15px]'}`}
                >
                  <span>{link.name}</span>
                  {link.hasMegaMenu && (
                    <ChevronDown className={`w-4 h-4 transition-transform duration-150 ${activeMenu === link.menuKey ? 'rotate-180 text-[#A27849]' : ''}`} />
                  )}
                </Link>
                {renderMegaMenu(link)}
              </div>
            ))}

            <div className={`flex items-center gap-4 ${isArabic ? 'border-r pr-4' : 'border-l pl-4'} border-[#A27849]/20`}>
              <a
                href="tel:+966122226665"
                className="text-[#1F3B64] hover:text-[#A27849] transition-colors duration-150"
                title="+966 12 222 6665"
              >
                <Phone className="w-4 h-4" />
              </a>
              <a
                href="mailto:info@wazin.law"
                className="text-[#1F3B64] hover:text-[#A27849] transition-colors duration-150"
                title="info@wazin.law"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>

            <button
              onClick={toggleLanguage}
              className="rounded-xl border-2 border-[#A27849] px-4 py-2 text-sm font-bold text-[#1F3B64] hover:bg-[#A27849] hover:text-white transition-all duration-150"
            >
              {text.languageSwitch}
            </button>

            {process.env.REACT_APP_MODE !== 'website' && (isAuthenticated() ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/dashboard"
                  className="rounded-xl bg-[#1F3B64] px-4 py-2 text-white font-bold hover:bg-[#17314f] transition-colors duration-150"
                >
                  <User className={`inline w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                  {text.account}
                </Link>
                <button
                  onClick={handleLogout}
                  className="rounded-xl bg-[#A27849] px-4 py-2 text-white font-bold hover:bg-[#8c653a] transition-colors duration-150"
                >
                  <LogOut className={`inline w-5 h-5 ${isArabic ? 'ml-2' : 'mr-2'}`} />
                  {text.logout}
                </button>
              </div>
            ) : (
              <Link
                to="/portals"
                className="rounded-xl bg-[#A27849] px-6 py-2.5 text-white font-bold hover:bg-[#8c653a] transition-colors duration-150"
              >
                {text.platform}
              </Link>
            ))}
          </div>

          <button
            className="md:hidden rounded-xl border-2 border-[#A27849] bg-[#A27849]/10 p-3 text-[#1F3B64] hover:bg-[#A27849] hover:text-white transition-all duration-150"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#A27849]/20 bg-white py-4" dir={isArabic ? 'rtl' : 'ltr'}>
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className="py-2 font-bold text-[#1F3B64] hover:text-[#A27849] transition-colors duration-150"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}

              <div className="flex flex-col gap-2 border-t border-[#A27849]/20 pt-4">
                <a href="tel:+966122226665" className="flex items-center gap-3 text-[#1F3B64] hover:text-[#A27849]">
                  <Phone className="w-5 h-5" />
                  <span dir="ltr">+966 12 222 6665</span>
                </a>
                <a href="mailto:info@wazin.law" className="flex items-center gap-3 text-[#1F3B64] hover:text-[#A27849]">
                  <Mail className="w-5 h-5" />
                  info@wazin.law
                </a>
              </div>

              <div className="flex gap-2 pt-4">
                <button
                  onClick={toggleLanguage}
                  className="flex-1 rounded-xl border-2 border-[#A27849] px-4 py-2 text-sm font-bold text-[#1F3B64] hover:bg-[#A27849] hover:text-white transition-all duration-150"
                >
                  {text.languageSwitch}
                </button>

                {process.env.REACT_APP_MODE !== 'website' && (isAuthenticated() ? (
                  <>
                    <Link
                      to="/dashboard"
                      className="flex-1 rounded-xl bg-[#1F3B64] px-4 py-2 text-center font-bold text-white"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {text.account}
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 rounded-xl bg-[#A27849] px-4 py-2 font-bold text-white"
                    >
                      {text.logout}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/portals"
                    className="flex-1 rounded-xl bg-[#A27849] px-4 py-2 text-center font-bold text-white"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {text.platform}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default NavbarSimple;
